'use client';

import type { ForwardedRef, ReactElement } from 'react';
import React, { useRef } from 'react';
import { Spinner } from 'components/Spinner';
import styled, { css } from 'styled-components';
import { mergeProps, mergeRefs } from '@react-aria/utils';
import type { IconProps } from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';
import type { ButtonProps, SharedButtonProps } from './Button2';
import { buttonReset, buttonTransitions } from './Button2';
import { isValidButtonProp } from './components/is-valid-button-prop';
import { disablePendingProps } from './isPending';
import { stripHandledEvents } from './stripHandledEvents';
import { useButton } from './useButton';

/**
 * Sizes
 */

export const iconSizes = {
  small: 16,
  regular: 24,
};

const iconButtonSizes = {
  small: css`
    height: ${theme.spaces[24]};
    width: ${theme.spaces[24]};
  `,
  regular: css`
    height: ${theme.spaces[32]};
    width: ${theme.spaces[32]};
  `,
};

const {
  button: { primary, secondary, secure, destructive, tertiary },
} = theme.colors.components;

export const iconButtonSizeMixin = css`
  &[data-size='small'] {
    ${iconButtonSizes.small}
  }
  &[data-size='regular'] {
    ${iconButtonSizes.regular}
  }
`;

/**
 * Variants
 */

type IconButtonVariants =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  /** @deprecated Use "dimmed" instead */
  | 'tertiary-dimmed'
  | 'dimmed'
  | 'secure'
  | 'destructive';

const iconButtonVariantsMixin = css`
  &[data-variant='primary'] {
    background: ${primary.background};
    color: ${primary.icon};

    &[data-hovered] {
      background: ${primary.interactive.background.hover};
    }

    &[aria-expanded='true'],
    &[data-pressed],
    &[data-loading] {
      background: ${primary.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: ${primary.interactive.background.disabled};
      color: ${theme.colors.icon.interactive.disabled};
    }
  }

  &[data-variant='secondary'] {
    background: ${secondary.background};
    color: ${secondary.icon};

    &[data-hovered] {
      background: ${secondary.interactive.background.hover};
    }

    &[aria-expanded='true'],
    &[data-pressed],
    &[data-loading] {
      background: ${secondary.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: ${secondary.interactive.background.disabled};
      color: ${theme.colors.icon.interactive.disabled};
      cursor: not-allowed;
    }
  }

  &[data-variant='tertiary'] {
    background: transparent;
    color: ${secondary.icon};

    &[data-hovered] {
      background: ${tertiary.interactive.background.hover};
    }

    &[data-pressed],
    &[aria-expanded='true'],
    &[data-loading] {
      background: ${tertiary.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: transparent;
      color: ${theme.colors.icon.interactive.disabled};
      cursor: not-allowed;
    }

    &:focus-visible,
    &[data-focus-visible] {
      outline-offset: 0px;
    }
  }

  &[data-variant='tertiary-dimmed'],
  &[data-variant='dimmed'] {
    background: transparent;
    color: ${theme.colors.icon.primary};

    &[data-hovered] {
      background: ${tertiary.interactive.background.hover};
    }

    &[data-pressed],
    &[aria-expanded='true']:not([data-suppress-expanded-style]),
    &[data-loading] {
      background: ${tertiary.interactive.background.pressed};
    }

    &[data-suppress-expanded-style][aria-expanded='true'][data-hovered]:not([aria-disabled='true']) {
      background: ${tertiary.interactive.background.hover};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: transparent;
      color: ${theme.colors.icon.interactive.disabled};
      cursor: not-allowed;
    }

    &:focus-visible,
    &[data-focus-visible] {
      outline-offset: 0px;
    }
  }

  &[data-variant='secure'] {
    background: ${secure.background};
    color: ${secure.icon};

    &[data-hovered] {
      background: ${secure.interactive.background.hover};
    }

    &[aria-expanded='true'],
    &[data-pressed],
    &[data-loading] {
      background: ${secure.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: ${secure.interactive.background.disabled};
      color: ${theme.colors.icon.interactive.disabled};
    }
  }

  &[data-variant='destructive'] {
    background: ${destructive.background};
    color: ${destructive.icon};

    &[data-hovered] {
      background: ${destructive.interactive.background.hover};
    }

    &[aria-expanded='true'],
    &[data-pressed],
    &[data-loading] {
      background: ${destructive.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: ${destructive.interactive.background.disabled};
      color: ${theme.colors.icon.interactive.disabled};
    }
  }
`;

/**
 * Shapes
 */

type IconButtonShapes = 'square' | 'circular';

export const iconButtonShapesMixin = css`
  &[data-shape='square'] {
    border-radius: ${theme.radii[6]};
  }
  &[data-shape='circular'] {
    border-radius: 50%;
  }
`;

/**
 * Focus styles
 */

export const iconButtonFocusMixin = css`
  &:focus-visible,
  &[data-focus-visible] {
    outline: 1.5px solid ${theme.colors.border.interactive.focused};
    outline-offset: 1.5px;
  }
`;

/**
 * Disabled styles
 */

export const iconButtonDisabledMixin = css`
  &[aria-disabled],
  &[data-disabled] {
    cursor: not-allowed;
  }
`;

/**
 * Layout styles
 */

export const iconButtonLayoutMixin = css`
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  outline: none;
  overflow: hidden;
  padding: 0;
  position: relative;
  vertical-align: middle;

  &[data-loading] {
    cursor: default;
  }

  &[data-loading] > *:not([role='progressbar']) {
    opacity: 0;
  }

  &[data-shared-hover-enabled] {
    background: transparent !important;
  }
`;

/**
 * Component
 */

export type IconButtonProps = {
  icon?: ReactElement<IconProps>;
  /** A label is required for accessibility. It is visually hidden. */
  label: string;
  /** The shape of the button. */
  shape?: IconButtonShapes;
  /** Toggle a spinner for the button. */
  isLoading?: boolean;
  /** A label to read when `isPending` is true. */
  loadingLabel?: string;
  /** The color and style of the button. */
  variant?: IconButtonVariants;
} & SharedButtonProps &
  Pick<ButtonProps, 'size'>;

function IconButton(
  props: IconButtonProps,
  forwardedRef: ForwardedRef<HTMLButtonElement>
) {
  const {
    children,
    elementType,
    icon,
    label,
    shape = 'square',
    size = 'regular',
    variant = 'secondary',
    isLoading,
    loadingLabel = 'Loading',
    isDisabled,
    ...otherProps
  } = disablePendingProps(props);

  const ref = useRef(null);

  const { buttonProps, hasSharedHover } = useButton(ref, {
    elementType,
    enableSharedHover:
      variant === 'tertiary' ||
      variant === 'tertiary-dimmed' ||
      variant === 'dimmed',
    isDisabled,
    ...otherProps,
  });

  const child = (icon ?? children) as ReactElement<any>;

  const renderedChild = (
    <>
      {React.cloneElement(child, {
        'aria-hidden': true,
        size: child.props?.size ?? iconSizes[size],
      })}
    </>
  );

  return (
    <StyledIconButton
      ref={mergeRefs(ref, forwardedRef)}
      aria-disabled={isLoading || undefined}
      aria-label={isLoading ? undefined : label}
      as={elementType}
      data-loading={isLoading || undefined}
      data-shape={shape}
      data-shared-hover-enabled={hasSharedHover || undefined}
      data-size={size}
      data-variant={variant}
      {...mergeProps(buttonProps, stripHandledEvents(props))}
    >
      {isLoading && (
        <Spinner
          aria-label={loadingLabel}
          centered
          delay={0}
          enterDuration={0}
          isLoading={true}
          size={size === 'regular' ? 'x-small' : 'xx-small'}
        />
      )}
      {renderedChild}
    </StyledIconButton>
  );
}

const StyledIconButton = styled.button.withConfig({
  shouldForwardProp: isValidButtonProp,
})`
  ${buttonReset};
  ${buttonTransitions};
  ${iconButtonVariantsMixin};
  ${iconButtonShapesMixin};
  ${iconButtonSizeMixin};
  ${iconButtonFocusMixin};
  ${iconButtonDisabledMixin};
  ${iconButtonLayoutMixin};
`;

/**
 * An icon button element is used when you want a button which only has
 * an icon in it.
 *
 * @see WAI-ARIA https://www.w3.org/WAI/ARIA/apg/patterns/button/
 * @see Docs     http://vapor.frame.io/components/button
 * @example
 * <IconButton label="Open menu">
 *   <IconArrowDown />
 * </IconButton>
 */

const _IconButton = React.forwardRef(IconButton);
_IconButton.displayName = 'IconButton';
export { _IconButton as IconButton };
